/**
 * Settings → Products — the price catalogue behind proposals
 * (2026-09-07, flag `proposals`; "Import from CRM" needs `crm_depth`).
 *
 * A product is a name + unit price (integer cents, USD) + optional
 * SKU / unit label / description. Quote lines in a proposal can start
 * from one; the line keeps its own copy of the price so a later
 * catalogue change never silently rewrites a shared proposal.
 *
 * Archive, never delete: an archived product stays on the proposals
 * that used it. Backend permission `products.manage` is admin-only by
 * default — members see the list and a note, no controls.
 */

import { useCallback, useEffect, useState } from 'react';
import { apiRequest, moneyLabel, parseMoneyToCents } from '../utils/apiRequest';

export default function ProductsSettings({ authToken, isAdmin, onToast, crmImportEnabled = false }) {
  const [products, setProducts] = useState(null);
  const [includeArchived, setIncludeArchived] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null); // null | 'new' | product
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);

  const toast = useCallback((m) => { if (typeof onToast === 'function') onToast(m); }, [onToast]);

  const load = useCallback(async () => {
    try {
      const r = await apiRequest(`/api/products${includeArchived ? '?includeArchived=1' : ''}`, { token: authToken });
      setProducts(r.products || []);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [authToken, includeArchived]);

  useEffect(() => { load(); }, [load]);

  const archive = async (p) => {
    if (!window.confirm(`Archive "${p.name}"? Proposals that already use it are unaffected.`)) return;
    setBusy(true);
    try {
      await apiRequest(`/api/products/${p.id}`, { token: authToken, method: 'DELETE' });
      toast(`"${p.name}" archived.`);
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const importFromCrm = async () => {
    setImporting(true);
    setError(null);
    try {
      const r = await apiRequest('/api/products/import-crm', { token: authToken, method: 'POST' });
      toast(`Imported from ${r.provider}: ${r.imported} new, ${r.updated} updated, ${r.skipped} skipped.`);
      await load();
    } catch (err) {
      if (err.code === 'crm_not_connected') setError('No CRM is connected. Connect HubSpot or Salesforce under Integrations first.');
      else if (err.code === 'crm_needs_reauth') setError(`${err.message}. Reconnect it under Integrations, then import again.`);
      else setError(err.message);
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="settings-section">
      <div className="settings-section-title">Products</div>
      <p className="settings-section-desc">
        Your price list. Quote tables in proposals start from these; each line keeps its own price, so editing here never changes a proposal you've already shared.
      </p>
      {!isAdmin && (
        <div className="settings-section-sub" style={{ marginBottom: 12 }}>Only company admins can edit the catalogue. You can see it below.</div>
      )}
      {error && <div role="alert" style={alertStyle}>{error}</div>}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', margin: '12px 0' }}>
        {isAdmin && <button type="button" onClick={() => setEditing('new')} style={primaryBtn} disabled={editing !== null}>+ Add product</button>}
        {isAdmin && crmImportEnabled && (
          <button type="button" onClick={importFromCrm} disabled={importing} style={ghostBtn}>{importing ? 'Importing…' : 'Import from CRM'}</button>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--room-text-muted, #64748b)', marginLeft: 'auto' }}>
          <input type="checkbox" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} /> Show archived
        </label>
      </div>

      {editing !== null && (
        <ProductForm
          authToken={authToken}
          product={editing === 'new' ? null : editing}
          onCancel={() => setEditing(null)}
          onSaved={async (p) => { setEditing(null); toast(`"${p.name}" saved.`); await load(); }}
        />
      )}

      {products === null ? (
        <div style={{ color: 'var(--room-text-muted, #64748b)' }}>Loading…</div>
      ) : products.length === 0 ? (
        <div style={cardStyle}><p style={{ margin: 0, color: 'var(--room-text-muted, #64748b)' }}>No products yet.</p></div>
      ) : (
        <div style={{ ...cardStyle, padding: 0, overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 14 }}>
            <thead>
              <tr style={{ background: 'var(--room-surface, #f8fafc)' }}>
                <th style={th}>Product</th><th style={th}>SKU</th><th style={{ ...th, textAlign: 'right' }}>Unit price</th><th style={th}>Per</th>{isAdmin && <th style={th} />}
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.id} style={{ borderTop: '1px solid var(--room-border-soft, #f1f5f9)', opacity: p.archivedAt ? 0.6 : 1 }}>
                  <td style={td}>
                    <div style={{ fontWeight: 600 }}>{p.name}{p.archivedAt && <span style={{ marginLeft: 8, fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, background: '#fef3c7', color: '#92400e' }}>Archived</span>}</div>
                    {p.description && <div style={{ fontSize: 13, color: 'var(--room-text-muted, #64748b)' }}>{p.description}</div>}
                  </td>
                  <td style={td}>{p.sku || '—'}</td>
                  <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{moneyLabel(p.unitPriceCents)}</td>
                  <td style={td}>{p.unitLabel || '—'}</td>
                  {isAdmin && (
                    <td style={{ ...td, textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" onClick={() => setEditing(p)} style={ghostBtn} disabled={busy}>Edit</button>
                      {!p.archivedAt && <button type="button" onClick={() => archive(p)} style={{ ...ghostBtn, marginLeft: 6, color: '#dc2626' }} disabled={busy}>Archive</button>}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProductForm({ authToken, product, onCancel, onSaved }) {
  const [name, setName] = useState(product ? product.name : '');
  const [sku, setSku] = useState(product ? (product.sku || '') : '');
  const [price, setPrice] = useState(product ? (product.unitPriceCents / 100).toFixed(2) : '');
  const [unitLabel, setUnitLabel] = useState(product ? (product.unitLabel || '') : '');
  const [description, setDescription] = useState(product ? (product.description || '') : '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const save = async (e) => {
    e.preventDefault();
    const cents = parseMoneyToCents(price);
    if (!name.trim()) { setError('Name is required.'); return; }
    if (!Number.isInteger(cents) || cents < 0) { setError('Enter a price like 1200 or 1200.50.'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = { name: name.trim(), sku: sku.trim() || null, unitPriceCents: cents, unitLabel: unitLabel.trim() || null, description: description.trim() || null };
      const r = product
        ? await apiRequest(`/api/products/${product.id}`, { token: authToken, method: 'PATCH', body })
        : await apiRequest('/api/products', { token: authToken, method: 'POST', body });
      onSaved(r.product);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} style={{ ...cardStyle, marginBottom: 14 }}>
      <div style={{ fontWeight: 600, marginBottom: 10 }}>{product ? `Edit ${product.name}` : 'New product'}</div>
      {error && <div role="alert" style={alertStyle}>{error}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <label style={labelStyle}>Name<input type="text" value={name} onChange={(e) => setName(e.target.value)} maxLength={200} style={inputStyle} /></label>
        <label style={labelStyle}>Unit price (USD)<input type="text" inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} style={inputStyle} placeholder="1200.00" /></label>
        <label style={labelStyle}>Per <span style={hint}>(seat, month…)</span><input type="text" value={unitLabel} onChange={(e) => setUnitLabel(e.target.value)} maxLength={60} style={inputStyle} /></label>
        <label style={labelStyle}>SKU<input type="text" value={sku} onChange={(e) => setSku(e.target.value)} maxLength={100} style={inputStyle} /></label>
      </div>
      <label style={{ ...labelStyle, marginTop: 12 }}>Description<textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} maxLength={2000} style={{ ...inputStyle, resize: 'vertical' }} /></label>
      <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
        <button type="submit" disabled={saving} style={primaryBtn}>{saving ? 'Saving…' : 'Save'}</button>
        <button type="button" onClick={onCancel} style={ghostBtn}>Cancel</button>
      </div>
    </form>
  );
}

const cardStyle = { background: 'var(--room-card, #fff)', border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 14, padding: 16, color: 'var(--room-text-strong, #0f172a)' };
const labelStyle = { display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--room-text-body, #334155)' };
const hint = { fontWeight: 400, color: 'var(--room-text-subtle, #94a3b8)' };
const inputStyle = { width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: 10, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-strong, #0f172a)', fontSize: 14, fontFamily: 'inherit', fontWeight: 400 };
const ghostBtn = { padding: '8px 14px', borderRadius: 10, border: '1px solid var(--room-border, #e2e8f0)', background: 'var(--room-card, #fff)', color: 'var(--room-text-body, #334155)', fontFamily: 'inherit', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const primaryBtn = { padding: '10px 18px', borderRadius: 10, border: '1px solid #7c3aed', background: '#7c3aed', color: '#fff', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, cursor: 'pointer' };
const alertStyle = { padding: '10px 12px', borderRadius: 10, background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#dc2626', fontSize: 14, marginBottom: 10 };
const th = { textAlign: 'left', padding: '10px 12px', fontWeight: 600, color: 'var(--room-text-muted, #64748b)', fontSize: 13 };
const td = { padding: '10px 12px', verticalAlign: 'top', color: 'var(--room-text-body, #334155)' };
